import { NgIf } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { cloneDeep } from 'lodash';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_VALIDATORS, Masks } from '@app/constants';
import { FormComponent, FormMode } from '@app/shared/forms/components';
import { UserService } from '@app/shared/services/user.service';
import { isNil, mapArrayToSelectOptions } from '@app/utils';
import { ManageUsersService } from '../manage-users.service';
import { AddUserModel } from '../models/add-user.model';
import { UserListItem } from '../models/user-list-item.model';

@Component({
	selector: 'sl-add-user-form',
	templateUrl: './add-user-form.component.html',
	styleUrl: './add-user-form.component.less',
	standalone: true,
	imports: [
    ReactiveFormsModule,
	FormComponent,
    FormlyModule,
    MatDialogActions,
    MatButtonModule,
    MatButton,
    MatDialogModule,
    NgIf
],
	providers: [UserService, ManageUsersService],
})
export class AddUserFormComponent {
	@ViewChild(FormComponent) formComponent: FormComponent;

	entity: AddUserModel = {
		Id: null,
		Email: null,
		UserName: null,
		FirstName: null,
		LastName: null,
		FullName: null,
		RoleId: null,
		PhoneNumber: null,
		SiteId: null,
		DuplicateFromUserId: null,
		Active: null,
		RoleDescription: null,
		OpenIdUser: null,
	};

	emptyEntity: AddUserModel = {
		Id: null,
		Email: null,
		UserName: null,
		FirstName: null,
		LastName: null,
		FullName: null,
		RoleId: null,
		PhoneNumber: null,
		SiteId: null,
		DuplicateFromUserId: null,
		Active: null,
		RoleDescription: null,
		OpenIdUser: null,
	};

	title: string;

	options: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: false,
			flyout: false,
			selects: {
				roles: [],
				sites: [],
				users: [],
				roleEntities: [],
			},
		},
	};

	error: string;

	fields: FormlyFieldConfig[];

	form = new UntypedFormGroup({});

	constructor(
		private readonly dialogRef: MatDialogRef<AddUserFormComponent>,
		private readonly userService: UserService,
		private readonly manageUsersService: ManageUsersService,
		@Inject(MAT_DIALOG_DATA) public data: { title: string; entity: AddUserModel; roles: any[]; sites: any[]; users: UserListItem[] }
	) {
		this.emptyEntity = cloneDeep(this.entity);
		this.entity = { ...data.entity };

		this.title = data.title;

		this.options.formState.selects.roleEntities = data.roles;

		this.options.formState.selects.roles = mapArrayToSelectOptions(data.roles, {
			value: 'Id',
			label: 'Name',
			order: 'asc',
			sort: 'label',
			all: false,
		});

		this.options.formState.selects.sites = mapArrayToSelectOptions(data.sites, {
			value: 'Id',
			label: 'SiteName',
			order: 'asc',
			sort: 'label',
			all: false,
		});

		this.options.formState.selects.users = data.users;

		this.fields = this.getFormFields();
	}

	onSubmit(): void {
		this.userService.add(this.entity).subscribe((result) => {
			this.error = null;
			if (result.isValid) {
				this.entity.Id = result.data;
				this.dialogRef.close(this.entity);
			} else {
				const errors = result.errors.join(', ');
				this.error = errors;
			}
		});
	}

	onCancel(): void {
		this.dialogRef.close(null);
	}

	private getOpenIdUser(field: FormlyFieldConfig): void {
		if (field.formControl.invalid) {
			this.manageUsersService.setOpenIdUser(field, null);
			this.setNextFocus(false);
			return;
		}

		this.userService.getOpenIdUser(field.formControl.value).subscribe({
			next: (openIdUser) => {
				this.manageUsersService.setOpenIdUser(field, openIdUser);
				this.setNextFocus(!isNil(openIdUser));
			},
			error: () => {
				this.manageUsersService.setOpenIdUser(field, null);
				this.setNextFocus(false);
			},
		});
	}

	private setNextFocus(isOpenIdUser: boolean): void {
		const controlName = isOpenIdUser ? 'FullName' : 'UserName';
		this.formComponent.setFocus(controlName);
	}

	getFormFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroup: [
					{
						key: 'Email',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-6',
						props: {
							label: 'Email Address',
							required: true,
							change: (field: FormlyFieldConfig) => {
								this.getOpenIdUser(field);
							},
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.email],
							duplicate: {
								expression: (c: AbstractControl) => {
									if (!c.dirty) {
										return true;
									}
									return !this.options.formState.selects.users.find((u: UserListItem) => u.Email === c.value);
								},
								message: 'Duplicate Email',
							},
						},
					},
					{
						key: 'UserName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-6',
						props: {
							label: 'User Name',
							required: true,
						},
						expressions: {
							'props.disabled': (field: FormlyFieldConfig) => this.manageUsersService.isOpenIdUser(field.model),
						},
					},
					{
						key: 'FirstName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-6',
						props: {
							label: 'First Name',
							required: true,
							change: (field: FormlyFieldConfig) => {
								this.manageUsersService.setFullName(field);
							},
						},
						expressions: {
							'props.disabled': (field: FormlyFieldConfig) => this.manageUsersService.isOpenIdUser(field.model),
						},
					},
					{
						key: 'LastName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-6',
						props: {
							label: 'Last Name',
							required: true,
							change: (field: FormlyFieldConfig) => {
								this.manageUsersService.setFullName(field);
							},
						},
						expressions: {
							'props.disabled': (field: FormlyFieldConfig) => this.manageUsersService.isOpenIdUser(field.model),
						},
					},
					{
						key: 'FullName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-6',
						props: {
							label: 'Full Name',
							required: true,
						},
					},
					{
						key: 'PhoneNumber',
						type: FORMLY_FIELD_TYPES.maskedInput,
						className: 'col-xs-6',
						props: {
							label: 'Phone Number',
							required: true,
							maxLength: 12,
							minLength: 12,
							maskString: Masks.Phone,
						},
					},
					{
						key: 'SiteId',
						type: FORMLY_FIELD_TYPES.select,
						className: 'col-xs-6',
						props: {
							label: 'Site',
							required: true,
						},
						expressions: {
							'props.options': 'formState.selects.sites',
						},
					},
					{
						key: 'RoleId',
						type: FORMLY_FIELD_TYPES.select,
						className: 'col-xs-6',
						props: {
							label: 'Role',
							required: true,
							change: (field: FormlyFieldConfig) => {
								const role = this.options.formState.selects.roleEntities.find((r) => r.Id === field.formControl.value);
								if (role) {
									field.formControl.parent.get('RoleDescription').setValue(role.Description);
								}
							},
						},
						expressions: {
							'props.options': 'formState.selects.roles',
						},
					},
					{
						key: 'RoleDescription',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-6',
						props: {
							label: 'Role Description',
							disabled: true,
						},
					},
				],
			},
		];
	}
}
